import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RESOURCE_STATUS, RESOURCE_TYPES } from "@/lib/fleetConstants";
import { FieldSelect } from "@/components/ui/field-select";
import { IncidentDialog, ResourceDialog } from "@/components/fleet/FleetDialogs";
import { Car, Plus, Search, Loader2, AlertTriangle, Wrench } from "lucide-react";

export default function Flotte() {
  const { orgId, can } = useAuth();
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [resOpen, setResOpen] = useState(false);
  const [incOpen, setIncOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["fleet", orgId, type, status, q],
    queryFn: async () => (await api.get("/api/fleet", { params: {
      organization_id: orgId, type: type || undefined, status: status || undefined, q: q || undefined } })).data,
    enabled: !!orgId,
  });
  const resources = data?.resources || [];
  const k = data?.kpis || {};

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="flotte-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Flotte</h1>
        <div className="flex gap-2">
          <button onClick={() => setIncOpen(true)} data-testid="report-incident-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><AlertTriangle className="h-4 w-4 text-amber-500" /> Signaler un incident</button>
          {can("fleet.manage") && <button onClick={() => setResOpen(true)} data-testid="new-resource-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle ressource</button>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Kpi label="Total ressources" value={k.total} testid="fleet-kpi-total" />
        <Kpi label="Disponibles" value={k.available} testid="fleet-kpi-available" tone="text-emerald-700" />
        <Kpi label="En activité" value={k.in_use} testid="fleet-kpi-inuse" tone="text-blue-700" />
        <Kpi label="En maintenance" value={k.maintenance} testid="fleet-kpi-maintenance" tone="text-amber-700" />
        <Kpi label="Hors service" value={k.out_of_service} testid="fleet-kpi-oos" tone="text-rose-700" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" data-testid="fleet-search" className="w-56 rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
        </div>
        <FieldSelect testId="fleet-filter-type" className="w-40" value={type || "ALL"} onChange={(v) => setType(v === "ALL" ? "" : v)} options={[{ value: "ALL", label: "Tout type" }, ...RESOURCE_TYPES.map((r) => ({ value: r, label: r }))]} />
        <FieldSelect testId="fleet-filter-status" className="w-44" value={status || "ALL"} onChange={(v) => setStatus(v === "ALL" ? "" : v)} options={[{ value: "ALL", label: "Tout statut" }, ...Object.entries(RESOURCE_STATUS).map(([kk, v]) => ({ value: kk, label: v.label }))]} />
      </div>

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : resources.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 px-6 text-center" data-testid="fleet-empty"><Car className="mx-auto h-9 w-9 text-slate-300" /><p className="mt-3 text-base font-semibold text-slate-700">Aucune ressource pour le moment</p><p className="mt-1 text-sm text-slate-400">Enregistrez vos véhicules ou équipements pour pouvoir les affecter aux réservations.</p>{can("fleet.manage") && <button onClick={() => setResOpen(true)} data-testid="fleet-empty-cta" className="mt-5 inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700"><Plus className="h-4 w-4" /> Nouvelle ressource</button>}</div>
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white lg:block">
            <table className="w-full text-sm" data-testid="fleet-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr>{["Référence", "Nom", "Type", "Statut", "Maintenance", "Incidents"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
              <tbody className="divide-y divide-slate-100">
                {resources.map((r) => (
                  <tr key={r.id} onClick={() => nav(`/flotte/${r.id}`)} data-testid={`fleet-row-${r.id}`} className="cursor-pointer hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-medium text-slate-700">{r.internal_reference}</td>
                    <td className="px-4 py-3 text-slate-700">{r.name}</td>
                    <td className="px-4 py-3 text-slate-500">{r.resource_type}</td>
                    <td className="px-4 py-3"><Badge s={r.display_status} /></td>
                    <td className="px-4 py-3 text-slate-500">{r.open_maintenance ? <span className="inline-flex items-center gap-1 text-amber-600"><Wrench className="h-3.5 w-3.5" />{r.open_maintenance}</span> : "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{r.open_incidents ? <span className="inline-flex items-center gap-1 text-rose-600"><AlertTriangle className="h-3.5 w-3.5" />{r.open_incidents}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:hidden">
            {resources.map((r) => (
              <button key={r.id} onClick={() => nav(`/flotte/${r.id}`)} data-testid={`fleet-card-${r.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-3 text-left">
                <div><p className="font-mono font-medium text-slate-800">{r.internal_reference}</p><p className="text-xs text-slate-500">{r.name} · {r.resource_type}</p></div>
                <Badge s={r.display_status} />
              </button>
            ))}
          </div>
        </>
      )}

      <ResourceDialog open={resOpen} onOpenChange={setResOpen} />
      <IncidentDialog open={incOpen} onOpenChange={setIncOpen} resources={resources} />
    </div>
  );
}

const Badge = ({ s }) => { const c = RESOURCE_STATUS[s] || RESOURCE_STATUS.AVAILABLE; return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${c.badge}`} data-testid={`status-badge-${s}`}>{c.label}</span>; };
const Kpi = ({ label, value, testid, tone = "text-slate-900" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}><p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p><p className={`mt-1.5 text-2xl font-bold ${tone}`}>{value ?? 0}</p></div>
);
